import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import useSWR from 'swr'

import { CaseStudyPropertyDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { serializeCacheKey } from '@/lib/contentful/utils'

import { isNonNullable } from '@/utils/isNonNullable'
import { toKebabCase } from '@/utils/string/toKebabCase'

export function useCaseStudyFilters() {
	const { locale, isPreview, query } = useRouter()

	const [industries, setIndustries] = useState<{ title: string; slug: string }[]>([])
	const [topics, setTopics] = useState<{ title: string; slug: string }[]>([])
	const [competitors, setCompetitors] = useState<{ title: string; slug: string }[]>([])
	const [activeIndustry, setActiveIndustry] = useState<{ title: string; slug: string } | null>(null)
	const [activeTopics, setActiveTopics] = useState<{ title: string; slug: string }[]>([])
	const [activeCompetitors, setActiveCompetitors] = useState<{ title: string; slug: string }[]>([])

	const { data } = useSWR(
		serializeCacheKey('caseStudyFilters', {
			locale
		}),
		() =>
			client({ preview: isPreview }).request(CaseStudyPropertyDocument, {
				locale,
				preview: isPreview
			})
	)

	useEffect(() => {
		if (!data?.industries) return

		setIndustries(
			data.industries.items
				.map((item) => {
					if (!item || !item.name) return null

					return {
						title: item.name,
						slug: toKebabCase(item.name)
					}
				})
				.filter(isNonNullable)
		)
	}, [data?.industries])

	useEffect(() => {
		if (!data?.topics) return

		setTopics(
			data.topics.items
				.map((item) => {
					if (!item || !item.name) return null

					return {
						title: item.name,
						slug: toKebabCase(item.name)
					}
				})
				.filter(isNonNullable)
		)
	}, [data?.topics])

	useEffect(() => {
		if (!data?.competitors) return

		setCompetitors(
			data.competitors.items
				.map((item) => {
					if (!item || !item.name) return null

					return {
						title: item.name,
						slug: toKebabCase(item.name)
					}
				})
				.filter(isNonNullable)
		)
	}, [data?.competitors])

	const [appliedIndustry, setAppliedIndustry] = useState<{ title: string; slug: string } | null>(
		null
	)
	const [appliedTopics, setAppliedTopics] = useState<{ title: string; slug: string }[]>([])
	const [appliedCompetitors, setAppliedCompetitors] = useState<{ title: string; slug: string }[]>(
		[]
	)
	const [appliedFilters, setAppliedFilters] = useState<
		{ title: string; slug: string; type: string }[]
	>([])

	useEffect(() => {
		const foundIndustry =
			industries.find((industry) => {
				if (!query.industries) return null

				if (Array.isArray(query.industries)) {
					return industry.slug === query.industries[0]?.replaceAll('/', '')
				}

				return industry.slug === query.industries.replaceAll('/', '')
			}) ?? null
		setAppliedIndustry(foundIndustry)
		setActiveIndustry(foundIndustry)
	}, [query.industries, industries])

	useEffect(() => {
		const foundTopic = topics.filter((topic) => {
			if (!query.topics) return false

			if (Array.isArray(query.topics)) {
				return query.topics.map((queryTopic) => queryTopic.replaceAll('/', '')).includes(topic.slug)
			}

			return topic.slug === query.topics.replaceAll('/', '')
		})
		setAppliedTopics(foundTopic)
		setActiveTopics(foundTopic)
	}, [query.topics, topics])

	useEffect(() => {
		const foundCompetitors = competitors.filter((competitor) => {
			if (!query.competitors) return false

			if (Array.isArray(query.competitors)) {
				return query.competitors
					.map((queryCompetitor) => queryCompetitor.replaceAll('/', ''))
					.includes(competitor.slug)
			}

			return competitor.slug === query.competitors.replaceAll('/', '')
		})
		setAppliedCompetitors(foundCompetitors)
		setActiveCompetitors(foundCompetitors)
	}, [query.competitors, competitors])

	useEffect(() => {
		setAppliedFilters([
			...(appliedIndustry ? [{ ...appliedIndustry, type: 'industry' }] : []),
			...appliedTopics.map((topic) => ({ ...topic, type: 'topic' })),
			...appliedCompetitors.map((competitor) => ({ ...competitor, type: 'competitor' }))
		])
	}, [appliedIndustry, appliedTopics, appliedCompetitors])

	return {
		industries,
		topics,
		competitors,
		appliedIndustry,
		appliedTopics,
		appliedCompetitors,
		appliedFilters,
		activeCompetitors,
		activeIndustry,
		activeTopics,
		setActiveIndustry,
		setActiveTopics,
		setActiveCompetitors
	}
}
